import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Avatar, Icon } from 'copilot-design-system';
import React from 'react';

import RightClickMenu from 'src/components/RightClickContextMenu';
import { InboxPageContext } from 'src/context/inboxPageContext';
import { InboxSidebarItemData } from 'src/legacy/components/Inbox/InboxSidebar';
import {
  useDeleteNotificationMutation,
  useUpdateNotificationMutation,
} from 'src/services/api/inboxApi';
import { getInitials } from 'src/utils/UserUtils';

import { ItemRenderProps } from '../Channels/BaseChannelSidebar';
import { AvatarInfoType } from '../Channels/ChannelSidebarItem';
import { ChannelListItem } from '../UI/ListItem/ChannelListItem';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    summaryText: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: theme.spacing(0.5),
      '& > *:last-child': {
        flex: 1,
      },
    },
  }),
);

export const getSidebarTitle = (title: string) =>
  title.charAt(0).toUpperCase() +
  (title.length > 1 ? title.slice(1).toLowerCase() : '');

export const InboxSidebarItem: React.FC<
  ItemRenderProps<InboxSidebarItemData>
> = (props) => {
  const { selectedNotification } = React.useContext(InboxPageContext);
  const { channelOption, onSelect } = props;
  const classes = useStyles();
  const {
    avatarInfo,
    isRead,
    title,
    id: notificationId,
    notificationEventType,
  } = channelOption;
  const [updateNotification] = useUpdateNotificationMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const isCompanyNotification =
    avatarInfo?.isCompanyUser && avatarInfo?.companyName;
  const sidebarTitle =
    notificationEventType === 'internalChat.mentioned'
      ? title
      : title.charAt(0).toUpperCase() +
        (title.length > 1 ? title.slice(1).toLowerCase() : '');

  const handleChannelClicked = () => {
    onSelect(channelOption);
  };

  const subTitleRenderer: React.FC = ({ children }) => {
    return (
      <div className={classes.summaryText}>
        <Avatar
          size="xs"
          src={avatarInfo?.imageUrl || ''}
          text={getInitials(avatarInfo?.companyName || avatarInfo?.name || '', {
            twoLetters: !isCompanyNotification,
          })}
          fallbackColor={avatarInfo?.fallbackColor || ''}
          variant={avatarInfo ? getAvatarVariant(avatarInfo) : 'circle'}
        />
        <>{children}</>
      </div>
    );
  };

  /**
   * This function gets the avatar variant based on the notification subtitle.
   * When the sender belongs to a company we append 'from' to the sender name
   * which is available in the avatarInfo.name property.
   * @param avatarData : sender avatar data
   * @returns avatar variant
   */
  const getAvatarVariant = (avatarData: AvatarInfoType) =>
    avatarData.isCompanyUser ? 'rounded' : 'circle';

  const markAsUnreadAction = {
    name: 'Mark as unread',
    icon: <Icon icon="EmailUnread" className="h-[13px] w-[13px]" />,
    onClick: () => {
      if (notificationId) {
        updateNotification({
          id: notificationId,
          isRead: false,
        });
      }
    },
  };

  const markAsReadAction = {
    name: 'Mark as read',
    icon: <Icon icon="EmailRead" className="h-[13px] w-[13px]" />,
    onClick: () => {
      if (notificationId) {
        updateNotification({
          id: notificationId,
          isRead: true,
        });
      }
    },
  };

  const deleteNotificationAction = {
    name: 'Delete notification',
    icon: <Icon icon="Trash" className="h-[13px] w-[13px]" />,
    isDelete: true,
    onClick: () => {
      if (notificationId) {
        deleteNotification({
          id: notificationId,
        });
      }
    },
  };

  return (
    <RightClickMenu
      actions={[
        isRead ? markAsUnreadAction : markAsReadAction,
        deleteNotificationAction,
      ]}
    >
      <ChannelListItem
        title={sidebarTitle}
        subTitleRenderer={subTitleRenderer}
        subTitle={`${avatarInfo?.name || ''}${
          avatarInfo?.companyName ? ` from ${avatarInfo?.companyName}` : ''
        }`}
        timestamp={channelOption.timestamp}
        selected={selectedNotification?.id === notificationId}
        onSelect={handleChannelClicked}
        unread={!isRead}
        isNotification={true}
      />
    </RightClickMenu>
  );
};
